import * as React from 'react';
import { Divider, Grid } from 'semantic-ui-react';

import { Resultados } from 'src/api';
import { nombreCompletoConNombresPrimero } from 'src/utilidades';
import {
    CursosInscritos,
    InformacionDeContacto,
    InformacionMatricula,
    InformacionPersonalAlumno,
    InformacionPersonalApoderado,
} from './componentes';
import {
    CorreosProtocolo,
    DatosAlumno,
    DatosApoderado,
    DatosEmergencia,
    DatosMatricula,
    DatosTelefono,
    TelefonosProtocolo,
} from './tipos';

type Props = Readonly<{
    cargando: boolean;
    alumno: DatosAlumno;
    emergencia: DatosEmergencia;
    apoderado: DatosApoderado;
    matricula: DatosMatricula;
    area: 'preu' | 'ingles' | 'japones' | 'aleman';
    horario?: Resultados<'detalleDelAlumno'>;
}>;

const InformacionGeneral = (props: Props) => {
    const { cargando, alumno, emergencia, apoderado, matricula, area, horario } = props;

    return (
        <>
            <Grid columns='2' divided>
                <Grid.Row>
                    <Grid.Column>
                        <InformacionPersonalAlumno {...alumno} />
                    </Grid.Column>

                    <Divider hidden />

                    <Grid.Column>
                        <InformacionDeContacto {...emergencia} />
                    </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                    <Grid.Column>
                        <InformacionPersonalApoderado {...apoderado} />
                    </Grid.Column>
                    <Grid.Column>
                        <InformacionMatricula {...matricula} />
                    </Grid.Column>
                </Grid.Row>

                {area === 'preu' && (
                    <Grid.Row>
                        <Grid.Column>
                            <CursosInscritos cargando={cargando} horario={horario} />
                        </Grid.Column>
                    </Grid.Row>
                )}
            </Grid>
        </>
    );
};

const AREAS = {
    preu: 'Preu',
    ingles: 'Inglés',
    aleman: 'Alemán',
    japones: 'Japonés',
};

type HOCProps = Readonly<{
    cargando: boolean;
    ficha: Resultados<'fichaAlumno'>;
    horario: Props['horario'];
}>;
// Componente de orden superior cuya única función es traducir la ficha de alumno.
export default class extends React.Component<HOCProps> {
    public render() {
        const { cargando, ficha, horario } = this.props;

        return (
            <InformacionGeneral
                cargando={cargando}
                alumno={this.alumno()}
                emergencia={this.emergencia()}
                apoderado={this.apoderado()}
                matricula={this.matricula()}
                area={ficha.alumno.matricula.area}
                horario={horario}
            />
        );
    }

    private matricula(): DatosMatricula {
        const { ficha } = this.props;
        return {
            numeroDeMatricula: ficha.alumno.matricula.numeroDeContrato.toString(),
            area: AREAS[ficha.alumno.matricula.area],
            plan:
                ficha.alumno.matricula.plan === 'anual'
                    ? 'Anual'
                    : ficha.alumno.matricula.plan === 'intensivo'
                    ? 'Intensivo'
                    : 'Intensivo vespertino',
            asignaturas: ficha.alumno.matricula.asignaturas,
            electivo: ficha.alumno.matricula.electivo,
            fechaDeContrato: ficha.alumno.matricula.fechaMatricula,
            observacion: ficha.alumno.matricula.observacion,
        };
    }

    private apoderado(): DatosApoderado {
        const { ficha: ficha } = this.props;

        const correos: Array<string> = todosLosCorreos(ficha.apoderado.personales.correos);
        const telefonos = todosLosTelefonos(ficha.apoderado.personales.telefonos);

        const direccionPersonal = ficha.apoderado.personales.direcciones.find(
            direccion => direccion.tipo === 'particular',
        )!;
        const direccionLaboral = ficha.apoderado.personales.direcciones.find(
            direccion => direccion.tipo === 'laboral' && direccion.calle !== '',
        );
        return {
            id: {
                tipo: ficha.apoderado.personales.rutPasaporte.tipo === 'rut' ? 'rut' : 'pasaporte',
                valor: ficha.apoderado.personales.rutPasaporte.valor,
            },
            nombreCompleto: nombreCompletoConNombresPrimero(ficha.apoderado.personales.nombre),
            fechaDeNacimiento: ficha.apoderado.otros.fechaNacimiento,
            correos,
            telefonos,
            direccionPersonal: {
                calle: direccionPersonal.calle,
                comuna: direccionPersonal.comuna.nombre,
                numero: direccionPersonal.numero,
                region: direccionPersonal.region.nombre,
                dpto: direccionPersonal.dpto,
                bloque: direccionPersonal.block,
                poblacion: direccionPersonal.poblacion,
            },
            direccionLaboral:
                direccionLaboral !== undefined
                    ? {
                          calle: direccionLaboral.calle,
                          comuna: direccionLaboral.comuna.nombre,
                          numero: direccionLaboral.numero,
                          region: direccionLaboral.region.nombre,
                          dpto: direccionLaboral.dpto,
                          bloque: direccionLaboral.block,
                          poblacion: direccionLaboral.poblacion,
                      }
                    : undefined,
            estadoCivil: ficha.apoderado.otros.estadoCivil,
            facebook: ficha.apoderado.otros.facebook,
            ocupacion: ficha.apoderado.otros.ocupacion,
            relacionConAlumno: ficha.apoderado.otros.relacionConAlumno,
        };
    }

    private emergencia(): DatosEmergencia {
        const { ficha } = this.props;

        const correos = [
            ficha.alumno.contactoDeEmergencia.correos.principal,
            ...ficha.alumno.contactoDeEmergencia.correos.otros,
        ];

        const telefonos = [
            {
                nombre: 'Móvil',
                codArea: '9',
                numero: ficha.alumno.contactoDeEmergencia.telefonos.movil.numero,
            },
        ];
        const otrosTelefonos = ficha.alumno.contactoDeEmergencia.telefonos.otros.map(telefono => ({
            nombre: telefono.nombre,
            codArea: telefono.codArea,
            numero: telefono.numero,
        }));
        if (otrosTelefonos.length > 0) telefonos.push(...otrosTelefonos);

        return {
            nombreCompleto: ficha.alumno.contactoDeEmergencia.nombreCompleto,
            relacionConAlumno: ficha.alumno.contactoDeEmergencia.relacionConAlumno,
            correos,
            telefonos,
        };
    }

    private alumno(): DatosAlumno {
        const { ficha } = this.props;
        const telefonos = todosLosTelefonos(ficha.alumno.personales.telefonos);
        const correos: Array<string> = todosLosCorreos(ficha.alumno.personales.correos);

        const direccionPersonal = ficha.alumno.personales.direcciones.find(
            direccion => direccion.tipo === 'particular',
        )!;
        const direccionLaboral = ficha.alumno.personales.direcciones.find(
            direccion => direccion.tipo === 'laboral' && direccion.calle !== '',
        );

        return {
            nombreCompleto: nombreCompletoConNombresPrimero(ficha.alumno.personales.nombre),
            fechaDeNacimiento: ficha.alumno.personales.fechaNacimiento,
            correos,
            telefonos,
            direccionPersonal: {
                calle: direccionPersonal.calle,
                comuna: direccionPersonal.comuna.nombre,
                numero: direccionPersonal.numero,
                region: direccionPersonal.region.nombre,
                dpto: direccionPersonal.dpto,
                bloque: direccionPersonal.block,
                poblacion: direccionPersonal.poblacion,
            },
            // TODO: Hay que mover la lógica que indique cuando la dirección laboral es realmente
            // indefinida al servidor
            direccionLaboral:
                direccionLaboral !== undefined
                    ? {
                          calle: direccionLaboral.calle,
                          comuna: direccionLaboral.comuna.nombre,
                          numero: direccionLaboral.numero,
                          region: direccionLaboral.region.nombre,
                          dpto: direccionLaboral.dpto,
                          bloque: direccionLaboral.block,
                          poblacion: direccionLaboral.poblacion,
                      }
                    : undefined,
            estadoCivil: ficha.alumno.personales.estadoCivil,
            facebook: ficha.alumno.otros.facebook,
            ocupacion: ficha.alumno.otros.ocupacion,
            viveCon: ficha.alumno.otros.viveCon,
            id: {
                tipo: ficha.alumno.personales.rutPasaporte.tipo === 'rut' ? 'rut' : 'pasaporte',
                valor: ficha.alumno.personales.rutPasaporte.valor,
            },
        };
    }
}

// TODO: Revisar esto. No está muy funcional que digamos y no estoy seguro de esos push.
function todosLosTelefonos(telefonosDesdeProtocolo: TelefonosProtocolo): Array<DatosTelefono> {
    const telefonos = [
        {
            nombre: 'Móvil',
            codArea: '9',
            numero: telefonosDesdeProtocolo.movil.numero,
        },
    ];
    if (telefonosDesdeProtocolo.fijo !== '') {
        telefonos.push({
            nombre: 'Fijo',
            codArea: '2',
            numero: telefonosDesdeProtocolo.fijo,
        });
    }
    const otrosTelefonos = telefonosDesdeProtocolo.otros.map(telefono => ({
        nombre: telefono.nombre,
        codArea: telefono.codArea,
        numero: telefono.numero,
    }));

    if (otrosTelefonos.length > 0) telefonos.push(...otrosTelefonos);
    return telefonos;
}

function todosLosCorreos(correosDesdeProtocolo: CorreosProtocolo) {
    return [correosDesdeProtocolo.principal, ...correosDesdeProtocolo.otros];
}



// WEBPACK FOOTER //
// ./src/modulos/Alumnos/paginas/FichaDeAlumno/paginas/InformacionGeneral/index.tsx